if (!customElements.get('vaillant-product-waitlist')) {
  customElements.define(
    'vaillant-product-waitlist',
    class VaillantProductWaitlist extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.emailInput = this.querySelector('input[type="email"]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.buttonLabel = this.querySelector('.vaillant-product__waitlist-button-label');
        this.buttonLoading = this.querySelector('.vaillant-product__waitlist-button-loading');
        this.feedback = this.querySelector('[data-waitlist-feedback]');

        this.form?.addEventListener('submit', this.onSubmit.bind(this));
      }

      get companyId() {
        return this.dataset.companyId?.trim() || '';
      }

      get listId() {
        return this.dataset.listId?.trim() || '';
      }

      get variantId() {
        return this.dataset.variantId?.trim() || '';
      }

      set variantId(value) {
        if (value) this.dataset.variantId = String(value);
      }

      getProfileProperties() {
        const properties = {};

        if (this.dataset.productTitle) {
          properties.product_name = this.dataset.productTitle;
        }

        if (this.dataset.productHandle) {
          properties.product_handle = this.dataset.productHandle;
        }

        if (this.dataset.productId) {
          properties.product_id = this.dataset.productId;
        }

        if (this.variantId) {
          properties.variant_id = this.variantId;
        }

        return properties;
      }

      setFeedback(message, type = 'success') {
        if (!this.feedback) return;

        this.feedback.textContent = message;
        this.feedback.hidden = !message;
        this.feedback.classList.toggle('is-error', type === 'error');
        this.feedback.classList.toggle('is-success', type === 'success');
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.disabled = isLoading;
        this.submitButton.classList.toggle('is-loading', isLoading);
        this.buttonLabel?.toggleAttribute('hidden', isLoading);
        this.buttonLoading?.toggleAttribute('hidden', !isLoading);
      }

      buildBackInStockPayload(email) {
        return {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                  },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.variantId}`,
                },
              },
            },
          },
        };
      }

      buildListSubscriptionPayload(email) {
        const properties = this.getProfileProperties();
        const profileAttributes = { email };

        if (Object.keys(properties).length > 0) {
          profileAttributes.properties = properties;
        }

        return {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Back in stock waitlist',
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.listId,
                },
              },
            },
          },
        };
      }

      identifyWithKlaviyoOnsite(email) {
        const properties = this.getProfileProperties();
        const identifyPayload = {
          $email: email,
          ...properties,
        };

        window._learnq = window._learnq || [];
        window._learnq.push(['identify', identifyPayload]);
        window._learnq.push([
          'track',
          'Back In Stock',
          {
            ProductName: this.dataset.productTitle || '',
            ProductHandle: this.dataset.productHandle || '',
            ProductID: this.dataset.productId || '',
            VariantID: this.variantId || '',
          },
        ]);
      }

      async postToKlaviyo(endpoint, payload) {
        const response = await fetch(`${endpoint}?company_id=${encodeURIComponent(this.companyId)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-07-15',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) return;

        let message = this.dataset.errorMessage || 'Something went wrong. Please try again.';

        try {
          const data = await response.json();
          message = data?.errors?.[0]?.detail || data?.message || message;
        } catch {
          // Keep default message.
        }

        throw new Error(message);
      }

      async subscribe(email) {
        if (!this.companyId) {
          throw new Error('Klaviyo is not configured.');
        }

        if (!this.variantId) {
          throw new Error('Please choose your size.');
        }

        await this.postToKlaviyo(
          'https://a.klaviyo.com/client/back-in-stock-subscriptions/',
          this.buildBackInStockPayload(email)
        );

        if (this.listId) {
          await this.postToKlaviyo(
            'https://a.klaviyo.com/client/subscriptions/',
            this.buildListSubscriptionPayload(email)
          );
        }

        this.identifyWithKlaviyoOnsite(email);
      }

      async onSubmit(event) {
        event.preventDefault();
        this.setFeedback('');

        const email = this.emailInput?.value.trim();

        if (!email || !this.emailInput?.checkValidity()) {
          this.emailInput?.focus();
          this.setFeedback('Please enter a valid email address.', 'error');
          return;
        }

        this.setLoading(true);

        try {
          await this.subscribe(email);
          this.setFeedback(this.dataset.successMessage || 'You are on the waiting list.', 'success');
          if (this.form) this.form.reset();
        } catch (error) {
          this.setFeedback(error.message || this.dataset.errorMessage, 'error');
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
